/**
 * Pipeline stage helpers — next-stage computation and stage labels.
 *
 * Spec §7.1 fixes the 18 stages:
 *
 *   1–5  Batch-level (Order Placed → In-house Inspection)
 *   6–18 Part-level   (Assembling → Cheque Cleared)
 *
 * Branching at stage 9 (Inspection):
 *   - outcome ACCEPTED      → skip 10 + 11, next is 12 (Delivered)
 *   - outcome OBSERVATION
 *     or REJECTED           → stage 10 (Re-inspection Offered), then 11
 *                             (Re-inspection), then 12 regardless
 *
 * Stage 16 (Invoice Submitted) is gated on BG Accepted (§8.2). The gate is
 * a precondition the user satisfies elsewhere; it does NOT change the
 * sequential next stage, so we still return 16 after 15.
 *
 * These helpers are used by:
 *   - D1's awaiting-action queue: "given the entity's current state, what
 *     stage should the user complete next?"
 *   - D2's entity kanban (planned): same logic, displayed in a column
 *     layout grouped by current stage
 */

export type StageEntityKind = 'PART' | 'BATCH';

/**
 * Compute the next pipeline stage number for an entity, or null if the
 * entity is complete (PART at stage 18, BATCH at stage 5).
 *
 * @param entityKind  'PART' or 'BATCH'
 * @param currentStage  Highest recorded stage number on the entity.
 *                      For a PART with no stages recorded but aggregate
 *                      gate open, pass 5 (the "edge of part stages") so
 *                      the function returns 6. For a PART with aggregate
 *                      gate NOT open, this function shouldn't be called —
 *                      the caller should skip such parts.
 * @param stage9Outcome  When currentStage === 9, the outcome controls the
 *                       branch. Pass null for ACCEPTED or to use the
 *                       default (skip re-inspection). Pass OBSERVATION or
 *                       REJECTED to route through stages 10 + 11.
 * @param hasSubParts    PART-only. When false, stage 6 (Assembling) is skipped
 *                       and the part moves from the aggregate gate to stage 7.
 */
export function nextStageNumber(
  entityKind: StageEntityKind,
  currentStage: number,
  stage9Outcome: 'ACCEPTED' | 'OBSERVATION' | 'REJECTED' | null = null,
  hasSubParts = true,
): number | null {
  if (entityKind === 'BATCH') {
    if (currentStage >= 5) return null;
    if (currentStage < 1) return 1;
    return currentStage + 1;
  }

  // PART
  if (currentStage < 5) {
    // Pre-aggregate-gate territory; caller should have skipped, but if
    // we're called here we return the obvious "next once gate opens".
    return hasSubParts ? 6 : 7;
  }
  if (currentStage === 5) return hasSubParts ? 6 : 7;
  // Variant B: parts without sub-parts do not need Assembling, so stage 6
  // is not a valid stopping point. If stale data or a direct caller supplies
  // currentStage=6 for such a part, advance to Packing / Labelling.
  if (currentStage === 6 && !hasSubParts) return 7;
  if (currentStage === 9) {
    return stage9Outcome === 'OBSERVATION' || stage9Outcome === 'REJECTED' ? 10 : 12;
  }
  if (currentStage === 11) return 12;
  if (currentStage >= 18) return null;
  return currentStage + 1;
}

/**
 * Short human-readable stage label for use in headers, columns, badges.
 * Matches the spec §7.1 table verbatim so users can refer to the spec
 * and find the same wording.
 */
export function stageLabel(stageNumber: number): string {
  switch (stageNumber) {
    case 1:
      return 'Order Placed';
    case 2:
      return 'Pre-shipment Inspection';
    case 3:
      return 'In Transit';
    case 4:
      return 'Arrived';
    case 5:
      return 'In-house Inspection';
    case 6:
      return 'Assembling';
    case 7:
      return 'Packing & Labelling';
    case 8:
      return 'Inspection offered';
    case 9:
      return 'Inspection';
    case 10:
      return 'Re-inspection Offered';
    case 11:
      return 'Re-inspection';
    case 12:
      return 'Delivered';
    case 13:
      return 'Functional Check';
    case 14:
      return 'I-Note Received';
    case 15:
      return 'CRV Issued';
    case 16:
      return 'Invoice Submitted';
    case 17:
      return 'Cheque Released';
    case 18:
      return 'Cheque Cleared';
    default:
      return `Stage ${stageNumber}`;
  }
}
